// src/pages/ChatPage.jsx
// Patient chat — primary touchpoint for the patient portal.
//
// Sprint 13 changes (surgical — existing architecture preserved):
//   1. Fetch patient summary on mount (triage-history, care-plan, previu-age in parallel)
//      → pass as `welcomeSummary` prop to ChatWindow for the welcome bubble
//   2. Read + clear sessionStorage('previu_chat_prompt') on mount
//      → pass as `initialPrompt` prop to ChatWindow (pre-fills textarea)
//   3. Wrap <ChatWindow> in <ErrorBoundary> — closes P0 open item from sprint plan
//   4. Fix type="button" on ChatError button
//
// Field name corrections (v2 — from live API inspection):
//   - previuAge     → previuAgeYears   (PatientPreviuAgePage.jsx line 54)
//   - delta         → ageDeltaYears    (PatientPreviuAgePage.jsx line 56)
//   - consensus_risk_level → consensusRiskLevel  (camelCase — PatientTriageResultPage.jsx)
//   - care plan open count → !item.completed     (PatientCarePlanPage.jsx — boolean, not status enum)
//
// React expert review:
//   ✅ useRef stale-closure guard on summary fetch
//   ✅ Patient from localStorage('previu_patient') — matches existing pattern
//   ✅ All fetch() calls inside standalone async helper only
//   ✅ Graceful degradation on any single endpoint failure
//   ✅ No Tailwind, no MUI, no hardcoded hex

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import ErrorBoundary from '../components/ErrorBoundary';
import ChatWindow from '../components/features/chat/ChatWindow';
import { useChat } from '../hooks/useChat';
import { buildTriageContext } from '../services/chatService';
import { fetchPatientSummary } from '../services/patientSummary';
import { usePatient } from '../context/PatientContext';
import Icon from '../components/Icon';

function getPatient() {
  try { return JSON.parse(localStorage.getItem('previu_patient')); }
  catch { return null; }
}

// ─── Skeletons & error states (unchanged from v1) ─────────────────────────────
function ChatSkeleton() {
  return (
    <div style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {[80, 60, 90, 50].map((w, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: i % 2 === 0 ? 'flex-start' : 'flex-end' }}>
          <div style={{
            width: `${w}%`, maxWidth: 320, height: 44,
            background: 'var(--bg-subtle)',
            borderRadius: i % 2 === 0 ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
            animation: 'pulse 1.5s ease-in-out infinite',
          }} />
        </div>
      ))}
      <style>{`@keyframes pulse { 0%,100%{opacity:.6} 50%{opacity:1} }`}</style>
    </div>
  );
}

function ChatError({ error, onRetry }) {
  const isSession = error?.toLowerCase?.().includes('session') ||
                    error?.toLowerCase?.().includes('401')     ||
                    error?.toLowerCase?.().includes('unauthorized');
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100%', padding: '40px 24px', gap: 12,
      fontFamily: 'var(--font-body)',
    }}>
      <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="chat" size={30} /></div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600, color: 'var(--text-primary)', textAlign: 'center' }}>
        {isSession ? 'Your session has expired' : 'Chat is unavailable right now'}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', maxWidth: 280, lineHeight: 1.6 }}>
        {isSession
          ? 'Please sign in again to continue your conversation.'
          : 'We could not connect to the chat service. This is usually temporary.'}
      </div>
      <button
        type="button"
        onClick={isSession ? () => window.location.href = '/login' : onRetry}
        style={{
          marginTop: 4, background: 'var(--brand-accent)',
          color: 'var(--text-inverse)', border: 'none', borderRadius: 8,
          padding: '10px 22px', fontSize: 13, fontWeight: 600,
          cursor: 'pointer', fontFamily: 'var(--font-body)',
        }}
      >
        {isSession ? 'Sign in again →' : 'Try again'}
      </button>
    </div>
  );
}

// ─── Main ChatPage ─────────────────────────────────────────────────────────────
export default function ChatPage() {
  const patient = useMemo(() => getPatient(), []);

  const [welcomeSummary, setWelcomeSummary] = useState(null); // null = loading
  // DEF-046: ?onboarding=incomplete is set by resolvePostLoginRoute for a
  // patient who started but did not finish onboarding. Dismissal is per-visit on
  // purpose — a persisted dismissal is how the nudge silently stops existing.
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const onboardingIncomplete = searchParams.get('onboarding') === 'incomplete';
  const [initialPrompt,  setInitialPrompt]  = useState('');
  const [autoSend,       setAutoSend]       = useState(false);

  // Build clinical context string for system prompt injection.
  // welcomeSummary is null while loading — triageContext will be null too,
  // so the first message gets "No triage results available yet" which is fine.
  // Once summary loads (usually < 1s) subsequent messages get full context.
  const triageContext = buildTriageContext(welcomeSummary);
  const { displayMessages, isLoading, error, sendMessage, stageUpload, confirmUploadType, clearChat } = useChat(patient, triageContext);

  const summaryFetchedRef = useRef(false);

  useEffect(() => {
    if (summaryFetchedRef.current) return;
    summaryFetchedRef.current = true;

    // Pre-fill from PatientTriageResultPage "Ask Previu" button, or from the
    // Today screen's "Ask about your health story" bar — which additionally
    // sets previu_chat_autosubmit since that's the patient's own already-
    // typed query, not a suggestion to review before sending.
    const savedPrompt = sessionStorage.getItem('previu_chat_prompt');
    if (savedPrompt) {
      setInitialPrompt(savedPrompt);
      sessionStorage.removeItem('previu_chat_prompt');
      if (sessionStorage.getItem('previu_chat_autosubmit')) {
        setAutoSend(true);
        sessionStorage.removeItem('previu_chat_autosubmit');
      }
    }

    if (!patient?.id) return;
    const token = localStorage.getItem('previu_token');
    if (!token) return;

    fetchPatientSummary(patient.id, token)
      .then(data => setWelcomeSummary(data))
      .catch(err => {
        console.error('ChatPage: summary fetch failed', err);
        // Graceful degradation — open chat with empty summary
        setWelcomeSummary({
          lastResult:       null,
          openActionItems:  0,
          totalActionItems: 0,
          previuAge:        null,
          previuAgeDelta:   null,
        });
      });
  }, [patient?.id]);

  if (!patient) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100dvh', fontFamily: 'var(--font-body)', color: 'var(--text-muted)',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="lock" size={30} /></div>
          <div>Please open the link sent to you on WhatsApp to start a chat.</div>
        </div>
      </div>
    );
  }

  // No breadcrumb: a single crumb with no parent is not navigation, it is the
  // page title said a second time — and it was saying it under a different name
  // from both the sidebar item and the chat header. The sidebar's active state
  // answers "where am I", the panel header names the assistant, and the
  // conversation gets the vertical space back.
  return (
    <PatientAppShell>
      {/* DEF-046 / TC-P002 — the third onboarding state.
          TC-P002 expects "Skipped → nudge banner". Both "skipped" and "never
          started" used to route to /patient/onboarding, so a patient who had
          deliberately skipped was sent back through the same forced flow every
          login with no way past it. resolvePostLoginRoute now sends them here
          with ?onboarding=incomplete, and this is the nudge — dismissible, and
          not in the way of what they came to do. */}
      {onboardingIncomplete && !nudgeDismissed && (
        <div className="alert" style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
          background: 'var(--amber-bg)', color: 'var(--amber)',
          border: '1px solid var(--amber)', borderRadius: 'var(--radius-md)',
          padding: '10px 14px', fontSize: 13, fontFamily: 'var(--font-body)',
        }}>
          <Icon name="clipboard" size={15} />
          <span style={{ flex: 1 }}>
            Your health profile isn&rsquo;t finished. Completing it improves the
            accuracy of your risk score.
          </span>
          <button
            type="button"
            onClick={() => navigate('/patient/onboarding')}
            style={{
              border: 'none', background: 'var(--amber)', color: 'var(--text-inverse)',
              borderRadius: 'var(--radius-sm)', padding: '6px 12px',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Finish now
          </button>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setNudgeDismissed(true)}
            style={{
              border: 'none', background: 'transparent', color: 'var(--amber)',
              fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: 2,
            }}
          >
            ✕
          </button>
        </div>
      )}
      {/* This full-bleeds the chat over .main-content's own chrome, so its
          negative margin has to match .main-content's real padding exactly
          — 28px/32px on desktop, 16px (mobile breakpoint) at the top/sides.
          On mobile that padding is NOT uniform though: `16px 16px 60px` —
          the 60px at the bottom is reserved to clear .patient-bottom-tabs,
          a `position: fixed` 60px bar (see index.css). A uniform -16px
          margin cancels that reserve along with the rest, so this div
          overflowed .main-content's actual visible height by 60px — which,
          since .main-content is itself a scroll container
          (`overflow: hidden auto`), left it scrollable, and the mount-time
          `scrollIntoView` call further down this component's own chat
          window scrolled that outer container down, sliding this div's
          header out from under .topbar's fixed 52px and most of the way
          back under it again from the other direction. Leaving the bottom
          margin at 0 and subtracting the same 60px from height keeps this
          div exactly as tall as .main-content's real visible area, so
          there's nothing left for it to scroll. */}
      {/* --content-banner-h (PatientLayout.js's MissingGenderBanner, 0px
          when it isn't showing) — the same fix as .chat-pane in index.css.
          This div's height assumed nothing else could render above it
          inside .main-content; the banner does, for any patient with no
          gender set, and without this it pushed the composer below the
          fold exactly the same way it did on Care Team. */}
      <style>{`
        .chatpage-fullbleed { height: calc(100dvh - var(--topbar-h) - var(--content-banner-h, 0px)); margin: -28px -32px; }
        @media (max-width: 768px) {
          .chatpage-fullbleed { height: calc(100dvh - var(--topbar-h) - 60px - var(--content-banner-h, 0px)); margin: -16px -16px 0; }
        }
      `}</style>
      <div className="chatpage-fullbleed" style={{
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {isLoading && displayMessages.length === 0 && <ChatSkeleton />}

        {error && !isLoading && (
          <ChatError error={error} onRetry={() => window.location.reload()} />
        )}

        {/* ErrorBoundary closes P0 open item (FIGMA_TO_CODE_CHECKLIST §5) */}
        {!error && (
          <ErrorBoundary label="Ask Previu">
            <ChatWindow
              displayMessages={displayMessages}
              isLoading={isLoading}
              onSend={sendMessage}
              onUpload={stageUpload}
              onConfirmUploadType={confirmUploadType}
              onClear={clearChat}
              patient={patient}
              welcomeSummary={welcomeSummary}
              summaryReady={welcomeSummary !== null}
              initialPrompt={initialPrompt}
              autoSend={autoSend}
            />
          </ErrorBoundary>
        )}
      </div>
    </PatientAppShell>
  );
}
